/**
 * Checkpoints — Lexiun Git per-case checkpoint monitor.
 *
 * A checkpoint-scoped lens on the centralized execution ledger. Every
 * case-workspace git checkpoint is produced by a `case-checkpoint`
 * HARNESS run, and every terminal harness run already forwards to
 * lexserver's `executionLedger` (harn-service LedgerForwarder →
 * POST /api/platform/executions/ingest). So this page is a FILTERED VIEW
 * over the shipped `GET /api/platform/executions` API — NOT a new
 * backend. Handoff:
 * `ai-docs/handoffs/lexiun-git-monitor-lexportal-handoff-2026-07-24.md`.
 *
 * The verification story (why this page exists): an operator opens
 * `/checkpoints`, scopes to a user/case (e.g. galvão), and WATCHES
 * checkpoint runs appear with `status:completed` as missions run — the
 * live proof that Lexiun Git checkpointing is firing on that container.
 * A `status:failed` row is a checkpoint that skipped (INV-G1 fail-open —
 * the case is never harmed, but it flags a repo/lock issue to look at).
 *
 * Filter contract: the API supports `kind` but NOT `harnessId` (see the
 * executions route — it reads orgId/userId/caseId/kind/status/from/to
 * only). So we pin `kind:harness` on the wire and keep only
 * `harnessId === "case-checkpoint"` client-side. The header count is
 * stated honestly as "N of M harness runs loaded" so the client-filter
 * is never mistaken for a global total. A server-side `harnessId` filter
 * is the optional §7 enhancement (server lane) if volume ever demands it.
 *
 * Phase 2 (git-implementation lane, harness-only + a build-lane roll)
 * gives each checkpoint a commit-descriptive `result.summary`
 * ("C2 run · 3 files · ⚠ mass-deletion · a1b2c3d"); harn-service already
 * promotes `result.summary` to the ledger row, so those richer headlines
 * appear here with NO code change. Until then the summary is the bland
 * manifest label "Case Checkpoint". `riskBadge()` already scans the
 * summary string for the `Lex-Risk` keywords so the ⚠ badge lights up
 * the moment Phase 2 rolls — forward-compatible, harmless now.
 *
 * Data path: `usePlatformApi()` → `/api/platform/executions[/:id]` (NOT
 * `useGatewayRpc` — this is centralized lexserver data). The SPA is
 * behind `RequirePlatformAdmin`; the API enforces `requirePlatformAdmin`
 * server-side too. Read-only. Window bounded to the ledger TTL (90 days).
 */

import { GitCommit, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ExecutionLedgerDetailPane } from "@/components/execution-ledger/ExecutionLedgerDetailPane";
import {
  EMPTY_EXECUTION_FILTER,
  ExecutionLedgerFilters,
  toApiFilter,
  type ExecutionLedgerFilterState,
} from "@/components/execution-ledger/ExecutionLedgerFilters";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import { PlatformApiError } from "@/lib/platform-api";
import type {
  ExecutionLedgerFilter,
  ExecutionLedgerRecord,
} from "@/types/execution-ledger";

/** The manifest `id` of the checkpoint harness. Every Lexiun Git
 *  checkpoint run carries this `harnessId` on its ledger record. */
export const CHECKPOINT_HARNESS_ID = "case-checkpoint";

const PAGE_SIZE = 50;

/** `Lex-Risk` flag keywords (lexiun-git.md §4). Phase 2 stamps these into
 *  the checkpoint `result.summary`; we scan for them so a destructive
 *  checkpoint is flagged the moment the enriched harness rolls. */
const RISK_KEYWORDS = ["mass-deletion", "truncation", "core-rewrite"] as const;

export function Checkpoints() {
  const api = usePlatformApi();
  const navigate = useNavigate();
  const { executionId: urlExecutionId } = useParams<{ executionId?: string }>();

  const [filter, setFilter] = useState<ExecutionLedgerFilterState>(
    EMPTY_EXECUTION_FILTER,
  );
  // `items` is every kind:harness record fetched so far; `checkpoints`
  // (derived below) is the client-filtered subset. Keeping the raw list
  // lets the header state "N of M harness runs loaded" honestly.
  const [items, setItems] = useState<ExecutionLedgerRecord[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Detail-pane state — fetched independently of the list so a deep-link
  // load (`/checkpoints/:id`) works even when the row isn't on the
  // current filter page. Mirrors ExecutionLedger.tsx.
  const [detailRecord, setDetailRecord] = useState<ExecutionLedgerRecord | null>(
    null,
  );
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailNotInView, setDetailNotInView] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const selectedId = urlExecutionId ?? null;
  const setSelectedId = useCallback(
    (id: string | null) => {
      navigate(id ? `/checkpoints/${id}` : "/checkpoints");
    },
    [navigate],
  );

  // Wire filter: the UI state minus `kind` (hidden), always pinned to
  // `harness`. Referentially stable across renders that don't change a
  // field so the load effect doesn't refire spuriously.
  const apiFilter = useMemo<ExecutionLedgerFilter>(
    () => ({ ...toApiFilter(filter), kind: "harness" }),
    [filter],
  );

  const load = useCallback(
    async (append: boolean, appendCursor?: string) => {
      try {
        if (!append) setLoading(true);
        const res = await api.listExecutions(apiFilter, {
          limit: PAGE_SIZE,
          cursor: append ? appendCursor : undefined,
        });
        setItems((prev) =>
          append ? [...prev, ...res.data.records] : res.data.records,
        );
        setCursor(res.data.nextCursor);
        setError(null);
      } catch (e) {
        setError(
          e instanceof PlatformApiError
            ? `${e.code}: ${e.message}`
            : e instanceof Error
              ? e.message
              : "Failed to load checkpoints",
        );
      } finally {
        setLoading(false);
      }
    },
    [api, apiFilter],
  );

  // Reload from cursor=null on any filter change OR api-ref change (token
  // refresh). Cursor is intentionally NOT a dep — pagination appends via
  // the explicit `appendCursor` arg.
  useEffect(() => {
    setCursor(null);
    void load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, apiFilter]);

  // Detail fetch — runs on URL-selection change. A 404 sets `notInView`
  // so the pane shows the friendly message rather than a hard error.
  useEffect(() => {
    if (!selectedId) {
      setDetailRecord(null);
      setDetailNotInView(false);
      setDetailError(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    setDetailNotInView(false);
    api
      .getExecution(selectedId)
      .then((res) => {
        if (cancelled) return;
        setDetailRecord(res.data.record);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof PlatformApiError && e.status === 404) {
          setDetailNotInView(true);
          setDetailRecord(null);
        } else {
          setDetailError(
            e instanceof PlatformApiError
              ? `${e.code}: ${e.message}`
              : e instanceof Error
                ? e.message
                : "Failed to load checkpoint detail",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, selectedId]);

  // Client-filter to the checkpoint harness, then group by case (newest
  // run first within each case, cases ordered by most-recent activity).
  const checkpoints = useMemo(() => filterCheckpoints(items), [items]);
  const groups = useMemo(() => groupByCase(checkpoints), [checkpoints]);
  const stats = useMemo(() => summarize(checkpoints), [checkpoints]);

  const hasMore = cursor !== null;

  return (
    <div className="flex h-full flex-col gap-4" data-testid="checkpoints-page">
      <header className="flex items-center gap-2">
        <GitCommit className="h-5 w-5 text-emerald-500" />
        <h1 className="text-xl font-semibold">Checkpoints</h1>
        <span className="text-sm text-slate-400 dark:text-slate-500">
          Lexiun Git per-case checkpoint runs (90-day window)
        </span>
      </header>

      <CheckpointSummary stats={stats} loading={loading && items.length === 0} />

      <ExecutionLedgerFilters value={filter} onChange={setFilter} hideKind />

      {error && (
        <div
          className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-400"
          data-testid="checkpoints-error"
        >
          {error}
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-4">
        <CheckpointList
          groups={groups}
          shownCount={checkpoints.length}
          loadedCount={items.length}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={() => load(true, cursor ?? undefined)}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
        {selectedId ? (
          <ExecutionLedgerDetailPane
            selectedId={selectedId}
            record={detailRecord}
            leaves={[]}
            loading={detailLoading}
            notInView={detailNotInView}
            error={detailError}
            onSelect={setSelectedId}
          />
        ) : (
          <div
            className="flex min-h-0 items-center justify-center rounded-md border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400 dark:border-slate-700"
            data-testid="checkpoints-detail-empty"
          >
            Select a checkpoint run to see its commit details, timing, and
            any repo-lock error.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Summary strip ─────────────────────────────────────────────────────

/** Keep only the checkpoint-harness records from a page of `kind:harness`
 *  ledger rows. The executions API has no `harnessId` filter (§7 optional
 *  server enhancement), so this client-filter is the checkpoint scope. */
export function filterCheckpoints(
  records: ExecutionLedgerRecord[],
): ExecutionLedgerRecord[] {
  return records.filter((r) => r.harnessId === CHECKPOINT_HARNESS_ID);
}

export interface CheckpointStats {
  total: number;
  failed: number;
  cases: number;
}

/** Checkpoint-scoped counts from the LOADED rows. Deliberately NOT the
 *  harness-wide `getExecutionStats({kind:"harness"})` — that would mix in
 *  every other harness. This is a loaded-window count (grows as you page),
 *  which is exactly what the "are checkpoints firing, any failing?"
 *  verification headline needs. */
export function summarize(checkpoints: ExecutionLedgerRecord[]): CheckpointStats {
  const cases = new Set<string>();
  let failed = 0;
  for (const c of checkpoints) {
    if (c.caseId) cases.add(c.caseId);
    if (c.status !== "completed" && c.status !== "canceled") failed += 1;
  }
  return { total: checkpoints.length, failed, cases: cases.size };
}

function CheckpointSummary({
  stats,
  loading,
}: {
  stats: CheckpointStats;
  loading: boolean;
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-4 rounded-md border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900"
      data-testid="checkpoints-summary"
    >
      <Stat label="Checkpoint runs" value={stats.total} loading={loading} />
      <Stat
        label="Failed / skipped"
        value={stats.failed}
        loading={loading}
        tone={stats.failed > 0 ? "danger" : "muted"}
      />
      <Stat label="Cases covered" value={stats.cases} loading={loading} />
      <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">
        Counts reflect the loaded window — page for more.
      </span>
    </div>
  );
}

function Stat({
  label,
  value,
  loading,
  tone = "default",
}: {
  label: string;
  value: number;
  loading: boolean;
  tone?: "default" | "muted" | "danger";
}) {
  const valueCls =
    tone === "danger"
      ? "text-rose-600 dark:text-rose-400"
      : tone === "muted" && value === 0
        ? "text-slate-400 dark:text-slate-500"
        : "text-slate-900 dark:text-slate-100";
  return (
    <div className="flex flex-col">
      <span className={`text-2xl font-semibold ${valueCls}`}>
        {loading ? "—" : value}
      </span>
      <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>
    </div>
  );
}

// ─── Case-grouped list ─────────────────────────────────────────────────

export interface CaseGroup {
  caseId: string;
  runs: ExecutionLedgerRecord[];
  latest: number;
}

/** Group checkpoint runs by `caseId`, newest run first within each group,
 *  groups ordered by most-recent activity. Runs with no `caseId` (should
 *  be rare — a checkpoint is always case-scoped) collapse under a single
 *  "(no case)" bucket rather than being dropped. */
export function groupByCase(checkpoints: ExecutionLedgerRecord[]): CaseGroup[] {
  const map = new Map<string, ExecutionLedgerRecord[]>();
  for (const c of checkpoints) {
    const key = c.caseId ?? "(no case)";
    const arr = map.get(key);
    if (arr) arr.push(c);
    else map.set(key, [c]);
  }
  const groups: CaseGroup[] = [];
  for (const [caseId, runs] of map) {
    runs.sort((a, b) => b.createdAt - a.createdAt);
    groups.push({ caseId, runs, latest: runs[0]?.createdAt ?? 0 });
  }
  groups.sort((a, b) => b.latest - a.latest);
  return groups;
}

interface ListProps {
  groups: CaseGroup[];
  shownCount: number;
  loadedCount: number;
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function CheckpointList({
  groups,
  shownCount,
  loadedCount,
  loading,
  hasMore,
  onLoadMore,
  selectedId,
  onSelect,
}: ListProps) {
  return (
    <div className="flex min-h-0 flex-col rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-700">
        <span className="text-sm font-semibold">
          {shownCount} checkpoint run{shownCount === 1 ? "" : "s"}
          <span className="ml-1 font-normal text-slate-400">
            of {loadedCount} harness run{loadedCount === 1 ? "" : "s"} loaded
          </span>
        </span>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>
      <div
        className="min-h-0 flex-1 overflow-y-auto"
        data-testid="checkpoints-list"
      >
        {!loading && shownCount === 0 && (
          <div className="p-6 text-center text-sm text-slate-400">
            No checkpoint runs in the current window. Checkpoints fire on
            mission open/terminal + idle sweeps — if a container has
            {" "}
            <code className="font-mono">LEX_CHECKPOINT_ENABLE=1</code> and is
            running missions, runs appear here. (Loaded {loadedCount} harness
            run{loadedCount === 1 ? "" : "s"}, none from the checkpoint
            harness.)
          </div>
        )}
        {groups.map((g) => (
          <CaseGroupSection
            key={g.caseId}
            group={g}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        ))}
        {hasMore && (
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loading}
            className="w-full border-t border-slate-200 py-2 text-sm font-medium text-indigo-600 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-indigo-400 dark:hover:bg-slate-800"
            data-testid="checkpoints-load-more"
          >
            {loading ? "Loading…" : "Load more"}
          </button>
        )}
      </div>
    </div>
  );
}

function CaseGroupSection({
  group,
  selectedId,
  onSelect,
}: {
  group: CaseGroup;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div data-testid={`checkpoints-case-group-${group.caseId}`}>
      <div className="sticky top-0 flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-xs dark:border-slate-800 dark:bg-slate-800/60">
        <span className="font-mono font-medium text-slate-700 dark:text-slate-300">
          {group.caseId}
        </span>
        <span className="text-slate-400">
          {group.runs.length} run{group.runs.length === 1 ? "" : "s"}
        </span>
      </div>
      {group.runs.map((r) => (
        <CheckpointRow
          key={r.executionId}
          record={r}
          selected={selectedId === r.executionId}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

function CheckpointRow({
  record,
  selected,
  onSelect,
}: {
  record: ExecutionLedgerRecord;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const risk = riskBadge(record.summary);
  const headline = checkpointHeadline(record);
  return (
    <button
      type="button"
      onClick={() => onSelect(record.executionId)}
      className={`flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800 ${
        selected ? "bg-indigo-50 dark:bg-indigo-950" : ""
      }`}
      data-testid={`checkpoints-row-${record.executionId}`}
    >
      <span className={statusPillCls(record.status)}>{record.status}</span>
      {risk && (
        <span
          className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-rose-800 dark:bg-rose-900 dark:text-rose-200"
          title="Destructive-change risk flag stamped on this checkpoint"
          data-testid="checkpoints-risk-badge"
        >
          ⚠ {risk}
        </span>
      )}
      <span className="truncate text-slate-700 dark:text-slate-300">
        {headline}
      </span>
      <span className="ml-auto shrink-0 text-xs text-slate-400">
        {fmtDuration(record.durationMs)} · {new Date(record.createdAt).toLocaleString()}
      </span>
    </button>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────

/**
 * The row headline for a checkpoint run.
 *
 * Reality check (verified against the live ledger 2026-07-25): today a
 * checkpoint's `title` is the manifest LABEL ("Case Checkpoint") and its
 * `summary` is the manifest DESCRIPTION (a verbose prose sentence) — NOT
 * the label, contrary to the handoff §3 wording. So Phase 1 must headline
 * on `title` ("Case Checkpoint"), never the verbose `summary`.
 *
 * Phase 2 replaces `summary` with a short, structured run line
 * ("C2 run · 3 files · ⚠ mass-deletion · a1b2c3d" / "noop (clean tree)" /
 * "skipped: lock-contention"). `looksLikeRunSummary()` recognises that
 * shape, so this picker upgrades to the rich summary the moment Phase 2
 * rolls — with no further code change.
 */
export function checkpointHeadline(record: {
  title?: string;
  summary?: string;
}): string {
  if (record.summary && looksLikeRunSummary(record.summary)) {
    return record.summary;
  }
  return record.title ?? record.summary ?? "Case Checkpoint";
}

/** Does this summary string match the Phase-2 run-summary shape (rather
 *  than the verbose Phase-1 manifest description)? The Phase-2 format uses
 *  " · " separators and leads with a commit class / noop / skipped. */
function looksLikeRunSummary(summary: string): boolean {
  const s = summary.trim();
  return s.includes("·") || /^(noop\b|skipped\b|C[123]\b)/i.test(s);
}

/** First `Lex-Risk` keyword found in the summary string, or null. Works
 *  against the Phase-2 enriched summary ("… · ⚠ mass-deletion · …");
 *  returns null for the bland Phase-1 label. */
export function riskBadge(summary?: string): string | null {
  if (!summary) return null;
  const lower = summary.toLowerCase();
  for (const kw of RISK_KEYWORDS) {
    if (lower.includes(kw)) return kw;
  }
  return null;
}

function fmtDuration(ms?: number): string {
  if (ms === undefined || ms === null) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3_600_000) return `${(ms / 60_000).toFixed(1)}m`;
  return `${(ms / 3_600_000).toFixed(2)}h`;
}

/** Terminal-status pill — mirrors ExecutionLedger.tsx. A checkpoint run
 *  reads `completed` (committed or clean-noop), `failed` (skipped —
 *  INV-G1 fail-open, case unharmed), or `canceled`. */
function statusPillCls(status: string): string {
  const base =
    "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide";
  if (status === "completed") {
    return `${base} bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200`;
  }
  if (status === "canceled") {
    return `${base} bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300`;
  }
  return `${base} bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200`;
}
